import { useState, useEffect } from "react";
import { useLocation } from "react-router";
import { useAuth } from "./AuthContext";
import {
  getReviewerScores,
  saveScore,
  uploadDocumentForApplicant,
} from "./utils/firebaseUtils";
import { useNavigate } from "react-router";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./config/firebase"; // Ensure this path is correct
import Modal from "./components/Modal";
import * as Sentry from "@sentry/react";
import "./applicationsubmission.css";

const ApplicationSubmission = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const applicant = location.state?.applicant;

  const [reviewers, setReviewers] = useState([]);

  const { profile } = useAuth();

  // scores
  const [gpaScore, setGpaScore] = useState(null);
  const [collegeLocationScore, setCollegeLocationScore] = useState(null);
  const [personalStatementScore, setPersonalStatementScore] = useState(null);
  const [firstLetterOfRecScore, setFirstLetterOfRecScore] = useState(null);
  const [secondLetterOfRecScore, setSecondLetterOfRecScore] = useState(null);
  const [communityServiceScore, setCommunityServiceScore] = useState(null);
  const [hasAMentorScore, setHasAMentorScore] = useState(null);
  const [currentMemberScore, setCurrentMemberScore] = useState(null);
  const [attendingHBCUScore, setAttendingHBCUScore] = useState(null);
  const [pastWinnerScore, setPastWinnerScore] = useState(null);

  // Missing documents upload state
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState({});

  const handleGpaChange = (event) => {
    const value = event.target.value;
    setGpaScore(value ? parseInt(value, 10) : null);
  };

  const handleCollegeLocationChange = (event) => {
    const value = event.target.value;
    setCollegeLocationScore(value ? parseInt(value, 10) : null);
  };

  const handlePersonalStatementChange = (event) => {
    const value = event.target.value;
    setPersonalStatementScore(value ? parseInt(value, 10) : null);
  };

  const handleFirstLetterOfRecChange = (event) => {
    const value = event.target.value;
    setFirstLetterOfRecScore(value ? parseInt(value, 10) : null);
  };

  const handleSecondLetterOfRecChange = (event) => {
    const value = event.target.value;
    setSecondLetterOfRecScore(value ? parseInt(value, 10) : null);
  };

  const handleCommunityServiceChange = (event) => {
    const value = event.target.value;
    setCommunityServiceScore(value ? parseInt(value, 10) : null);
  };

  const handleHasAMentorChange = (event) => {
    const value = event.target.value;
    setHasAMentorScore(value ? parseInt(value, 10) : null);
  };

  const handleCurrentMemberChange = (event) => {
    const value = event.target.value;
    setCurrentMemberScore(value ? parseInt(value, 10) : null);
  };

  const handleAttendingHBCUChange = (event) => {
    const value = event.target.value;
    setAttendingHBCUScore(value ? parseInt(value, 10) : null);
  };

  const handlePastWinnerChange = (event) => {
    const value = event.target.value;
    setPastWinnerScore(value ? parseInt(value, 10) : null);
  };

  const handleSubmit = async () => {
    if (!applicant?.uid || !profile?.uid)
      return alert("Something wen wrong, please refresh the page");

    // GPA Check
    if (gpaScore === null) return alert("Please enter a valid GPA score.");
    if (gpaScore < 0 || gpaScore > 15) return alert("Invalid GPA score");

    // college location check
    if (
      collegeLocationScore === null &&
      !(applicant?.applicantLevel === "High School Senior")
    )
      return alert(
        "Please enter a score for if the student is in-state or out of state.",
      );

    if (
      (collegeLocationScore < 3 || collegeLocationScore > 5) &&
      !(applicant?.applicantLevel === "High School Senior")
    )
      return alert("Invalid score given for being in-state or out of state");

    // personal statement check
    if (
      personalStatementScore === null ||
      personalStatementScore < 0 ||
      personalStatementScore > 25
    )
      return alert("Invalid score given for personal statement");

    // 1st letter of rec statement check
    if (
      (firstLetterOfRecScore === null ||
        firstLetterOfRecScore < 0 ||
        firstLetterOfRecScore > 25) &&
      !(applicant?.applicantLevel === "High School Senior")
    )
      return alert("Invalid score given for 1st letter of recommendation");

    if (
      (firstLetterOfRecScore === null ||
        firstLetterOfRecScore < 0 ||
        firstLetterOfRecScore > 20) &&
      applicant?.applicantLevel === "High School Senior"
    )
      return alert("Invalid score given for 1st letter of recommendation");

    // 2nd letter of rec statement check
    if (
      (secondLetterOfRecScore === null ||
        secondLetterOfRecScore < 0 ||
        secondLetterOfRecScore > 25) &&
      !(applicant?.applicantLevel === "High School Senior")
    )
      return alert("Invalid score given for 2nd letter of recommendation");

    if (
      (secondLetterOfRecScore === null ||
        secondLetterOfRecScore < 0 ||
        secondLetterOfRecScore > 20) &&
      applicant?.applicantLevel === "High School Senior"
    )
      return alert("Invalid score given for 2nd letter of recommendation");

    if (
      communityServiceScore === null ||
      communityServiceScore < 0 ||
      communityServiceScore > 10
    )
      return alert("Invalid score given for community service");

    if (hasAMentorScore === null || hasAMentorScore < 0 || hasAMentorScore > 5)
      return alert("Invalid score given for if applicant has a mentor");

    // ADD THIS CHECK - Only validate currentMemberScore for non-High School Seniors
    if (
      (currentMemberScore === null ||
        currentMemberScore < 0 ||
        currentMemberScore > 10) &&
      !(applicant?.applicantLevel === "High School Senior")
    )
      return alert(
        "Invalid score given for if applicant is a member or legacy member.",
      );

    if (
      (attendingHBCUScore === null ||
        attendingHBCUScore < 0 ||
        attendingHBCUScore > 10) &&
      applicant?.applicantLevel === "High School Senior"
    )
      return alert(
        "Invalid score given for if applicant will be attending an HBCU",
      );

    if (
      (pastWinnerScore === null ||
        pastWinnerScore < 0 ||
        pastWinnerScore > 5) &&
      !(applicant?.applicantLevel === "High School Senior")
    )
      return alert("Invalid score given for if applicant is a past recipient");

    setLoading(true);
    await saveScore(
      applicant,
      profile,
      navigate,
      gpaScore,
      collegeLocationScore,
      personalStatementScore,
      firstLetterOfRecScore,
      secondLetterOfRecScore,
      communityServiceScore,
      hasAMentorScore,
      currentMemberScore,
      attendingHBCUScore,
      pastWinnerScore,
    );
    setLoading(false);
  };

  // load inputs
  useEffect(() => {
    if (!applicant?.uid || !profile?.uid) return;
    const currentYear = new Date().getFullYear().toString();

    const reviewDocRef = doc(
      db,
      "Submissions",
      "ScholarshipApplications",
      currentYear.toString(),
      applicant.uid,
      "reviewers",
      profile.uid,
    );

    const fetchReviewData = async () => {
      try {
        const reviewDoc = await getDoc(reviewDocRef);
        if (reviewDoc.exists()) {
          const data = reviewDoc.data();
          if (data.gpaScore !== undefined) {
            setGpaScore(data.gpaScore);
          }
          if (data.collegeLocationScore !== undefined) {
            setCollegeLocationScore(data.collegeLocationScore);
          }
          if (data.personalStatementScore !== undefined) {
            setPersonalStatementScore(data.personalStatementScore);
          }
          if (data.firstLetterOfRecScore !== undefined) {
            setFirstLetterOfRecScore(data.firstLetterOfRecScore);
          }
          if (data.secondLetterOfRecScore !== undefined) {
            setSecondLetterOfRecScore(data.secondLetterOfRecScore);
          }
          if (data.communityServiceScore !== undefined) {
            setCommunityServiceScore(data.communityServiceScore);
          }
          if (data.hasAMentorScore !== undefined) {
            setHasAMentorScore(data.hasAMentorScore);
          }
          if (data.currentMemberScore !== undefined) {
            setCurrentMemberScore(data.currentMemberScore);
          }
          if (data.attendingHBCUScore !== undefined) {
            setAttendingHBCUScore(data.attendingHBCUScore);
          }
          if (data.pastWinnerScore !== undefined) {
            setPastWinnerScore(data.pastWinnerScore);
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error fetching review data:", error);
      }
    };

    fetchReviewData();
  }, [applicant?.uid, profile?.uid]);

  // load the reviewers
  useEffect(() => {
    const loadReviewers = async () => {
      const currentYear = new Date().getFullYear().toString();
      const results = await getReviewerScores(applicant, currentYear); // or dynamic year
      setReviewers(results);
    };

    if (applicant?.uid) {
      loadReviewers();
    }
  }, [applicant]);

  const calculateAge = (birthday) => {
    const birthDate = new Date(birthday);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // Adjust age if birthday hasn't occurred yet this year
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  // Check if user is a reviewer
  const isReviewer = profile?.roles?.includes("reviewer");
  const isAdmin = profile?.roles?.includes("giveReviewAccess");

  // Define all possible documents
  const allDocuments = [
    { label: "Personal Statement", field: "writtenStatementUrl" },
    { label: "Community Service", field: "communityServiceUrl" },
    { label: "School ID", field: "schoolIdUrl" },
    { label: "Transcript", field: "transcriptUrl" },
    { label: "1st Letter of Recommendation", field: "letter1Url" },
    { label: "2nd Letter of Recommendation", field: "letter2Url" },
  ];

  // Add college acceptance letter for high school seniors
  if (applicant?.applicantLevel === "High School Senior") {
    allDocuments.push({
      label: "College Acceptance Letter",
      field: "collegeAcceptanceLetterUrl",
    });
  }

  // Get missing documents
  const missingDocuments = allDocuments.filter(
    (doc) => !applicant?.[doc.field],
  );

  // Handle document upload
  const handleDocumentUpload = async (fieldName, file) => {
    if (!file) return;

    // Validate file type (PDF or image)
    const validTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!validTypes.includes(file.type)) {
      alert("Please upload a PDF or image file (JPEG, JPG, or PNG)");
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      alert("File size must be less than 10MB");
      return;
    }

    setUploadingDoc(fieldName);
    try {
      await uploadDocumentForApplicant(file, fieldName, applicant.uid);
      setUploadSuccess((prev) => ({ ...prev, [fieldName]: true }));
      alert("Document uploaded successfully!");

      // Fetch updated applicant data from Firestore
      const currentYear = new Date().getFullYear();
      const applicationRef = doc(
        db,
        "Submissions",
        "ScholarshipApplications",
        currentYear.toString(),
        applicant.uid,
      );

      const updatedDoc = await getDoc(applicationRef);
      if (updatedDoc.exists()) {
        // Update the applicant state with fresh data
        navigate("/submission-dashboard/application-submission", {
          state: { applicant: { id: updatedDoc.id, ...updatedDoc.data() } },
          replace: true,
        });
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      Sentry.captureException(error);
      alert("Failed to upload document. Please try again.");
    } finally {
      setUploadingDoc(null);
    }
  };

  if (!applicant) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "black",
        }}
      >
        <p>No applicant data found.</p>
      </div>
    );
  }

  return (
    <div className="application-submission">
      <h1>
        {applicant?.firstName} {applicant?.lastName}
      </h1>

      <div className="applicant-level-and-submission-date">
        <h3>
          {applicant?.applicantLevel} <br /> {applicant?.email}
        </h3>

        <h3>
          {applicant?.submitted && (
            <p className="submitted-on">
              Submitted on{": "}
              {applicant?.updatedAt
                ? new Date(
                    applicant.updatedAt.seconds
                      ? applicant.updatedAt.seconds * 1000 // Firestore Timestamp
                      : applicant.updatedAt, // Regular Date String or Timestamp
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })
                : "Unknown"}
            </p>
          )}
        </h3>
      </div>

      <div className="applicant-submission-explanation">
        <div>
          <p>
            The rubric is at the bottom right of the screen where it says
            "Reviewed by." Or, use these links if you prefer to open the rubrics
            in a new tab{" "}
            <span
              onClick={() =>
                window.open(
                  "https://firebasestorage.googleapis.com/v0/b/rho-scholarships.firebasestorage.app/o/rubric%2F2024%20High%20School%20Scholarship%20Application%20Scoring%20Rubric.pdf?alt=media&token=195f4d8f-855c-4996-81a2-aab17152a109",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              style={{ textDecoration: "underline", cursor: "pointer" }}
            >
              High School rubric
            </span>
            {" | "}
            <span
              onClick={() =>
                window.open(
                  "https://firebasestorage.googleapis.com/v0/b/rho-scholarships.firebasestorage.app/o/rubric%2F2024%20College%20Scholarship%20Application%20Scoring%20Rubric.pdf?alt=media&token=afd4b7cb-5a96-4c56-a94b-d303ea11d888",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              style={{ textDecoration: "underline", cursor: "pointer" }}
            >
              College rubric
            </span>
          </p>
        </div>
      </div>

      <div className="personal-and-school-info">
        <div
          style={{
            minHeight:
              applicant?.applicantLevel === "High School Senior"
                ? "300px"
                : "400px",
          }}
        >
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>Date of Birth:</span>{" "}
            {applicant.birthday
              ? new Date(applicant.birthday).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "2-digit",
                })
              : "N/A"}{" "}
            {" | "}
            {" Age: "}
            {applicant.birthday ? calculateAge(applicant.birthday) : "N/A"}
          </p>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>Phone Number:</span>{" "}
            {applicant?.cellphone}
          </p>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>Home Address:</span>
            <br />
            {applicant?.address}
          </p>
        </div>
        <div>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>School:</span>{" "}
            {applicant?.schoolName}
          </p>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>Major:</span>{" "}
            {applicant?.major || "N/A"}
            {" | "}
            <span style={{ fontFamily: "gotham-medium" }}>Minor:</span>{" "}
            {applicant?.minor || "N/A"}
            {" | "}
            <span style={{ fontFamily: "gotham-medium" }}>GPA:</span>{" "}
            {applicant?.gpa || "None Provided"}
            {!(applicant?.applicantLevel === "High School Senior") ? (
              <>
                {" | "}
                <span style={{ fontFamily: "gotham-medium" }}>Grade:</span>{" "}
                {applicant?.academicClassification}
              </>
            ) : null}
          </p>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>School Address:</span>
            <br />
            {applicant?.schoolAddress}
          </p>

          {applicant?.applicantLevel === "High School Senior" ? (
            <div>
              <p>For this section, grade the applicant based on GPA.</p>
              <p>Min Score: 0 | Max Score: 15 | View rubric for more details</p>
              <input
                min={0}
                max={15}
                type="number"
                style={{ marginBottom: 10, height: 30, width: 150 }}
                placeholder="Enter score here"
                value={gpaScore} // Ensure empty input if null
                onChange={handleGpaChange}
              />
            </div>
          ) : (
            <>
              <div>
                <p>For this section, grade the applicant based on GPA.</p>
                <p>
                  Min Score: 0 | Max Score: 15 | View rubric for more details
                </p>
                <input
                  min={0}
                  max={15}
                  type="number"
                  style={{ marginBottom: 10, height: 30, width: 150 }}
                  placeholder="Enter score here"
                  value={gpaScore} // Ensure empty input if null
                  onChange={handleGpaChange}
                />
              </div>
              <div>
                <p>
                  For this section, grade the applicant based on College
                  Location.
                </p>
                <p>
                  Min Score: 3 | Max Score: 5 | View rubric for more details
                </p>
                <input
                  min={3}
                  max={5}
                  type="number"
                  style={{ marginBottom: 10, height: 30, width: 150 }}
                  placeholder="Enter score here"
                  value={collegeLocationScore} // Ensure empty input if null
                  onChange={handleCollegeLocationChange}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="career-goal-and-involvement">
        <div>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>Career Goals:</span>
            <br />
            {applicant?.careerGoals}
          </p>
        </div>
        <div>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>
              Community or School Involvement:
            </span>
            <br />
            {applicant?.involvement}
          </p>
        </div>
      </div>

      <div className="questions">
        <div style={{ minHeight: "225px" }}>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>
              Have you received a scholarship from Rho Chapter, Alpha Phi Alpha
              Fraternity Inc.
            </span>
            <br />
            {applicant?.previousWinner} {applicant?.yearReceived}
          </p>
          {!(applicant?.applicantLevel === "High School Senior") && (
            <div>
              <p>
                For this section, grade the applicant based on if they are a
                past scholarship recipient.
              </p>
              <p>Min Score: 0 | Max Score: 5 | View rubric for more details</p>
              <input
                min={0}
                max={5}
                type="number"
                style={{ marginBottom: 10, height: 30, width: 150 }}
                placeholder="Enter score here"
                value={pastWinnerScore} // Ensure empty input if null
                onChange={handlePastWinnerChange}
              />
            </div>
          )}
        </div>
        <div>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>
              Do you have a mentor who is a member of Alpha Phi Alpha?
            </span>
            <br />
            {applicant?.alphaMentor} {applicant?.mentorName}
          </p>
          <div>
            <p>
              For this section, grade the applicant based on if they have a
              mentor.
            </p>
            <p>Min Score: 0 | Max Score: 5 | View rubric for more details</p>
            <input
              min={0}
              max={5}
              type="number"
              style={{ marginBottom: 10, height: 30, width: 150 }}
              placeholder="Enter score here"
              value={hasAMentorScore} // Ensure empty input if null
              onChange={handleHasAMentorChange}
            />
          </div>
        </div>
      </div>

      <div className="questions">
        <div style={{ minHeight: "230px" }}>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>
              Do you have a family member who is a member of Alpha Phi Alpha?
            </span>
            <br />
            {applicant?.alphaFamilyMember} <br />
            {applicant?.familyMemberName}
          </p>
        </div>
        {applicant?.applicantLevel !== "High School Senior" && (
          <div>
            <p>
              <span style={{ fontFamily: "gotham-medium" }}>
                Are you a current college brother?
              </span>
              <br />
              {applicant?.applicantLevel === "Current College Brother"
                ? "Yes"
                : "No"}
            </p>
            <div>
              <p>
                For this section, grade the applicant based on if they're a
                college brother.
              </p>
              <p>Min Score: 0 | Max Score: 10 | View rubric for more details</p>
              <input
                min={0}
                max={10}
                type="number"
                style={{ marginBottom: 10, height: 30, width: 150 }}
                placeholder="Enter score here"
                value={currentMemberScore} // Ensure empty input if null
                onChange={handleCurrentMemberChange}
              />
            </div>
          </div>
        )}
      </div>

      {applicant?.applicantLevel === "High School Senior" && (
        <>
          <div className="questions">
            <div>
              <p>
                <span style={{ fontFamily: "gotham-medium" }}>
                  Colleges that you have been accepted to
                </span>
                <br />
                {applicant?.collegesAccepted}
              </p>
              <div>
                <p>
                  For this section, grade the applicant based on if they will be
                  attending or shows an indication that they will be attending
                  an HBCU
                </p>
                <p>
                  Min Score: 0 | Max Score: 10 | View rubric for more details
                </p>
                <input
                  min={0}
                  max={10}
                  type="number"
                  style={{ marginBottom: 10, height: 30, width: 150 }}
                  placeholder="Enter score here"
                  value={attendingHBCUScore} // Ensure empty input if null
                  onChange={handleAttendingHBCUChange}
                />
              </div>
            </div>
            <div style={{ minHeight: "225px" }}>
              <p>
                <span style={{ fontFamily: "gotham-medium" }}>
                  Colleges pending acceptance
                </span>
                <br />
                {applicant?.collegesPending}
              </p>
            </div>
          </div>
        </>
      )}

      <div className="questions">
        <div>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>
              Personal Statement
            </span>
          </p>
          <br />
          <button
            onClick={() => {
              setFileUrl(applicant?.writtenStatementUrl);
              setIsOpen(true);
            }}
            className="submit-btn"
          >
            View Personal Statement
          </button>
          <button
            onClick={() =>
              window.open(applicant?.writtenStatementUrl, "_blank")
            }
            className="submit-btn"
            style={{ margin: "10px 0px" }} // Optional: Adds some spacing between buttons
          >
            Open Personal Statement in New Tab
          </button>
          <div>
            <p>
              For this section, grade the applicant based on their personal
              statement.
            </p>
            <p>Min Score: 0 | Max Score: 25 | View rubric for more details</p>
            <input
              min={0}
              max={25}
              type="number"
              style={{ marginBottom: 10, height: 30, width: 150 }}
              placeholder="Enter score here"
              value={personalStatementScore} // Ensure empty input if null
              onChange={handlePersonalStatementChange}
            />
          </div>
          <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
            {fileUrl ? (
              fileUrl.includes("alt=media") ? (
                <iframe
                  src={fileUrl}
                  width="100%"
                  height="500px"
                  style={{ border: "none" }}
                />
              ) : (
                <img
                  src={fileUrl}
                  alt="Preview"
                  style={{ maxWidth: "100%", maxHeight: "500px" }}
                />
              )
            ) : (
              <p style={{ color: "#000" }}>None submitted</p>
            )}
            <button
              style={{ margin: "10px 0px" }}
              className="submit-btn"
              onClick={() => setIsOpen(false)}
            >
              CLOSE
            </button>
          </Modal>
        </div>
        <div>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>
              Community Service Verification
            </span>
          </p>
          <br />
          <button
            onClick={() => {
              setFileUrl(applicant?.communityServiceUrl);
              setIsOpen(true);
            }}
            className="submit-btn"
          >
            View Community Service
          </button>
          <button
            onClick={() =>
              window.open(applicant?.communityServiceUrl, "_blank")
            }
            className="submit-btn"
            style={{ margin: "10px 0px" }} // Optional: Adds some spacing between buttons
          >
            Open Community Service in New Tab
          </button>
          <div>
            <p>
              For this section, grade the applicant based on their Community
              Service and Community/School Involvement, which is listed a few
              boxes above.
            </p>
            <p>Min Score: 0 | Max Score: 10 | View rubric for more details</p>
            <input
              min={0}
              max={10}
              type="number"
              style={{ marginBottom: 10, height: 30, width: 150 }}
              placeholder="Enter score here"
              value={communityServiceScore} // Ensure empty input if null
              onChange={handleCommunityServiceChange}
            />
          </div>
          <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
            {fileUrl ? (
              fileUrl.includes("alt=media") ? (
                <iframe
                  src={fileUrl}
                  width="100%"
                  height="500px"
                  style={{ border: "none" }}
                />
              ) : (
                <img
                  src={fileUrl}
                  alt="Preview"
                  style={{ maxWidth: "100%", maxHeight: "500px" }}
                />
              )
            ) : (
              <p style={{ color: "#000" }}>None submitted</p>
            )}
            <button
              style={{ margin: "10px 0px" }}
              className="submit-btn"
              onClick={() => setIsOpen(false)}
            >
              CLOSE
            </button>
          </Modal>
        </div>
      </div>

      <div className="questions">
        <div>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>
              Current School ID
            </span>
          </p>
          <br />
          <button
            onClick={() => {
              setFileUrl(applicant?.schoolIdUrl);
              setIsOpen(true);
            }}
            className="submit-btn"
          >
            View School ID
          </button>
          <button
            onClick={() => window.open(applicant?.schoolIdUrl, "_blank")}
            className="submit-btn"
            style={{ margin: "10px 0px" }} // Optional: Adds some spacing between buttons
          >
            Open School ID in New Tab
          </button>
          <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
            {fileUrl ? (
              fileUrl.includes("alt=media") ? (
                <iframe
                  src={fileUrl}
                  width="100%"
                  height="500px"
                  style={{ border: "none" }}
                />
              ) : (
                <img
                  src={fileUrl}
                  alt="Preview"
                  style={{ maxWidth: "100%", maxHeight: "500px" }}
                />
              )
            ) : (
              <p style={{ color: "#000" }}>None submitted</p>
            )}
            <button
              style={{ margin: "10px 0px" }}
              className="submit-btn"
              onClick={() => setIsOpen(false)}
            >
              CLOSE
            </button>
          </Modal>
        </div>
        <div>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>Transcript</span>
          </p>
          <br />
          <button
            onClick={() => {
              setFileUrl(applicant?.transcriptUrl);
              setIsOpen(true);
            }}
            className="submit-btn"
          >
            View Transcript
          </button>
          <button
            onClick={() => window.open(applicant?.transcriptUrl, "_blank")}
            className="submit-btn"
            style={{ margin: "10px 0px" }} // Optional: Adds some spacing between buttons
          >
            Open Transcript in New Tab
          </button>
          <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
            {fileUrl ? (
              fileUrl.includes("alt=media") ? (
                <iframe
                  src={fileUrl}
                  width="100%"
                  height="500px"
                  style={{ border: "none" }}
                />
              ) : (
                <img
                  src={fileUrl}
                  alt="Preview"
                  style={{ maxWidth: "100%", maxHeight: "500px" }}
                />
              )
            ) : (
              <p style={{ color: "#000" }}>None submitted</p>
            )}
            <button
              style={{ margin: "10px 0px" }}
              className="submit-btn"
              onClick={() => setIsOpen(false)}
            >
              CLOSE
            </button>
          </Modal>
        </div>
      </div>

      <div className="questions">
        <div>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>
              1st Letter of Recommendation
            </span>
          </p>
          <br />
          <button
            onClick={() => {
              setFileUrl(applicant?.letter1Url);
              setIsOpen(true);
            }}
            className="submit-btn"
          >
            View 1st Recommendation
          </button>
          <button
            onClick={() => window.open(applicant?.letter1Url, "_blank")}
            className="submit-btn"
            style={{ margin: "10px 0px" }} // Optional: Adds some spacing between buttons
          >
            Open 1st Recommendation in New Tab
          </button>
          {applicant?.applicantLevel === "High School Senior" ? (
            <div>
              <p>
                For this section, grade the applicant based on their 1st
                Recommendation Letter
              </p>
              <p>Min Score: 0 | Max Score: 20 | View rubric for more details</p>
              <input
                min={0}
                max={20}
                type="number"
                style={{ marginBottom: 10, height: 30, width: 150 }}
                placeholder="Enter score here"
                value={firstLetterOfRecScore} // Ensure empty input if null
                onChange={handleFirstLetterOfRecChange}
              />
            </div>
          ) : (
            <div>
              <p>
                For this section, grade the applicant based on their 1st
                Recommendation Letter
              </p>
              <p>Min Score: 0 | Max Score: 25 | View rubric for more details</p>
              <input
                min={0}
                max={25}
                type="number"
                style={{ marginBottom: 10, height: 30, width: 150 }}
                placeholder="Enter score here"
                value={firstLetterOfRecScore} // Ensure empty input if null
                onChange={handleFirstLetterOfRecChange}
              />
            </div>
          )}
          <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
            {fileUrl ? (
              fileUrl.includes("alt=media") ? (
                <iframe
                  src={fileUrl}
                  width="100%"
                  height="500px"
                  style={{ border: "none" }}
                />
              ) : (
                <img
                  src={fileUrl}
                  alt="Preview"
                  style={{ maxWidth: "100%", maxHeight: "500px" }}
                />
              )
            ) : (
              <p style={{ color: "#000" }}>None submitted</p>
            )}
            <button
              style={{ margin: "10px 0px" }}
              className="submit-btn"
              onClick={() => setIsOpen(false)}
            >
              CLOSE
            </button>
          </Modal>
        </div>
        <div>
          <p>
            <span style={{ fontFamily: "gotham-medium" }}>
              2nd Letter of Recommendation
            </span>
          </p>
          <br />
          <button
            onClick={() => {
              setFileUrl(applicant?.letter2Url);
              setIsOpen(true);
            }}
            className="submit-btn"
          >
            View 2nd Recommendation
          </button>
          <button
            onClick={() => window.open(applicant?.letter2Url, "_blank")}
            className="submit-btn"
            style={{ margin: "10px 0px" }} // Optional: Adds some spacing between buttons
          >
            Open 2nd Recommendation in New Tab
          </button>
          {applicant?.applicantLevel === "High School Senior" ? (
            <div>
              <p>
                For this section, grade the applicant based on their 2nd
                Recommendation Letter
              </p>
              <p>Min Score: 0 | Max Score: 20 | View rubric for more details</p>
              <input
                min={0}
                max={20}
                type="number"
                style={{ marginBottom: 10, height: 30, width: 150 }}
                placeholder="Enter score here"
                value={secondLetterOfRecScore} // Ensure empty input if null
                onChange={handleSecondLetterOfRecChange}
              />
            </div>
          ) : (
            <div>
              <p>
                For this section, grade the applicant based on their 2nd
                Recommendation Letter
              </p>
              <p>Min Score: 0 | Max Score: 25 | View rubric for more details</p>
              <input
                min={0}
                max={25}
                type="number"
                style={{ marginBottom: 10, height: 30, width: 150 }}
                placeholder="Enter score here"
                value={secondLetterOfRecScore} // Ensure empty input if null
                onChange={handleSecondLetterOfRecChange}
              />
            </div>
          )}
          <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
            {fileUrl ? (
              fileUrl.includes("alt=media") ? (
                <iframe
                  src={fileUrl}
                  width="100%"
                  height="500px"
                  style={{ border: "none" }}
                />
              ) : (
                <img
                  src={fileUrl}
                  alt="Preview"
                  style={{ maxWidth: "100%", maxHeight: "500px" }}
                />
              )
            ) : (
              <p style={{ color: "#000" }}>None submitted</p>
            )}
            <button
              style={{ margin: "10px 0px" }}
              className="submit-btn"
              onClick={() => setIsOpen(false)}
            >
              CLOSE
            </button>
          </Modal>
        </div>
      </div>

      {applicant?.applicantLevel === "High School Senior" && (
        <div className="questions">
          <div>
            <p>
              <span style={{ fontFamily: "gotham-medium" }}>
                College Acceptance Letter
              </span>
            </p>
            <br />
            <button
              onClick={() => {
                setFileUrl(applicant?.collegeAcceptanceLetterUrl);
                setIsOpen(true);
              }}
              className="submit-btn"
            >
              View Acceptance Letter
            </button>
            <button
              onClick={() =>
                window.open(applicant?.collegeAcceptanceLetterUrl, "_blank")
              }
              className="submit-btn"
              style={{ margin: "10px 0px" }} // Optional: Adds some spacing between buttons
            >
              Open Acceptance Letter in New Tab
            </button>
            <Modal isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
              {fileUrl ? (
                fileUrl.includes("alt=media") ? (
                  <iframe
                    src={fileUrl}
                    width="100%"
                    height="500px"
                    style={{ border: "none" }}
                  />
                ) : (
                  <img
                    src={fileUrl}
                    alt="Preview"
                    style={{ maxWidth: "100%", maxHeight: "500px" }}
                  />
                )
              ) : (
                <p style={{ color: "#000" }}>None submitted</p>
              )}
              <button
                style={{ margin: "10px 0px" }}
                className="submit-btn"
                onClick={() => setIsOpen(false)}
              >
                CLOSE
              </button>
            </Modal>
          </div>
        </div>
      )}

      <div className="reviewed-by">
        <h2>Reviewed by</h2>
        {reviewers.length > 0 ? (
          <>
            {reviewers.map((reviewer) => (
              <div key={reviewer.uid} className="reviewer-item">
                <p>
                  {reviewer.name} -{" "}
                  {reviewers.length >= 2 && isAdmin
                    ? `${reviewer.score} pts`
                    : "Score hidden"}
                </p>
              </div>
            ))}
            {reviewers.length < 2 && isAdmin && (
              <p className="score-notice">
                Scores available after 2 reviews have been submitted.
              </p>
            )}
          </>
        ) : (
          <p>No reviews yet</p>
        )}

        {isAdmin && (
          <h4>
            Average:{" "}
            {reviewers && reviewers.length >= 2
              ? (
                  reviewers.reduce(
                    (total, reviewer) => total + reviewer.score,
                    0,
                  ) / reviewers.length
                ).toFixed(2)
              : reviewers.length === 1
                ? ""
                : "N/A"}
          </h4>
        )}
        <button
          onClick={() => {
            setFileUrl(
              applicant?.applicantLevel === "High School Senior"
                ? "https://firebasestorage.googleapis.com/v0/b/rho-scholarships.firebasestorage.app/o/rubric%2F2024%20High%20School%20Scholarship%20Application%20Scoring%20Rubric.pdf?alt=media&token=195f4d8f-855c-4996-81a2-aab17152a109"
                : "https://firebasestorage.googleapis.com/v0/b/rho-scholarships.firebasestorage.app/o/rubric%2F2024%20College%20Scholarship%20Application%20Scoring%20Rubric.pdf?alt=media&token=afd4b7cb-5a96-4c56-a94b-d303ea11d888",
            );
            setIsOpen(true);
          }}
          className="submit-btn"
        >
          View Rubric
        </button>
      </div>

      <div style={{ margin: "25px" }}>
        <button onClick={handleSubmit} className="submit-btn">
          {loading ? "Submitting..." : "Submit my scores"}
        </button>
      </div>

      {/* Missing Documents Section - Only shown to reviewers */}
      {isAdmin && missingDocuments.length > 0 && (
        <div className="missing-documents-section">
          <h2 style={{ color: "#bb8811", fontFamily: "gotham-bold" }}>
            Missing Documents
          </h2>
          <p style={{ color: "#333", marginBottom: "20px" }}>
            The following documents have not been uploaded yet. You can upload
            them on behalf of the applicant. * Do note: past recipients don't
            need a recommendation letter
          </p>
          <p style={{ color: "#333", marginBottom: "20px" }}>
            Submitting a missing document will refresh the page. So if you have
            unsubmitted scores, please submit them before uploading a missing
            document.
          </p>

          <div className="missing-docs-container">
            {missingDocuments.map((doc) => (
              <div key={doc.field} className="missing-doc-item">
                <div className="missing-doc-header">
                  <span
                    style={{ fontFamily: "gotham-medium", color: "#bb8811" }}
                  >
                    {doc.label}
                  </span>
                  {uploadSuccess[doc.field] && (
                    <span style={{ color: "green", fontSize: "14px" }}>
                      ✓ Uploaded
                    </span>
                  )}
                </div>
                <div className="missing-doc-actions">
                  <input
                    type="file"
                    id={`upload-${doc.field}`}
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleDocumentUpload(doc.field, file);
                      }
                    }}
                    disabled={uploadingDoc === doc.field}
                  />
                  <label
                    htmlFor={`upload-${doc.field}`}
                    className="upload-doc-btn"
                    style={{
                      opacity: uploadingDoc === doc.field ? 0.6 : 1,
                      cursor:
                        uploadingDoc === doc.field ? "not-allowed" : "pointer",
                    }}
                  >
                    {uploadingDoc === doc.field
                      ? "Uploading..."
                      : "Choose & Upload File"}
                  </label>
                </div>
                <p
                  style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}
                >
                  Accepted: PDF, JPG, JPEG, PNG (Max 10MB)
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationSubmission;
